import { getRoot } from "@gadgetinc/mobx-quick-tree";
import type { Dominion } from "../../database/Dominion";
import { GadgetSystemDominionId, ShopifyDominionId } from "../../database/DominionLiterals";
import type { ModelField } from "../../database/ModelField";
import { GadgetSessionShopifySIDKey, GadgetSessionToCustomerKey, GadgetSessionToShopKey } from "./keys";
import { getIdentificationKeysForModel } from "./ShopifyRemoteIdFieldKeys";
import { FieldType } from "../../type-system/FieldType";

interface ShopifyDominion extends Dominion {
  id: typeof ShopifyDominionId;
}

// Field keys for Shopify dominion fields that can only be deleted when there is no active Shopify connection
export const conditionallyRemovableShopifyDominionFieldKeys = [
  GadgetSessionShopifySIDKey,
  GadgetSessionToShopKey,
  GadgetSessionToCustomerKey,
];

export const ShopifyDominion: ShopifyDominion = {
  id: ShopifyDominionId,
  name: "Shopify",
  sentenceName: "Shopify",
  description: "Data where Shopify is the source of truth",
  supportsMetadata() {
    return false;
  },
  supportsFieldValidations() {
    return false;
  },
  supportsFieldRemoval(field: ModelField) {
    if (field.fieldType === FieldType.ID || field.apiIdentifier === "id") {
      return false;
    }
    if (field.shopifyFieldKey === "id" || field.shopifyGraphQLFieldKey === "id") {
      return false;
    } else if (conditionallyRemovableShopifyDominionFieldKeys.includes(field.key)) {
      const root = getRoot(field);
      const activeShopifyConnection = root.maybeGetShopifyConnection();
      if (field.key === GadgetSessionToCustomerKey) {
        return !activeShopifyConnection?.configuration?.customerAuthenticationEnabled;
      }
      return !activeShopifyConnection;
    } else {
      // TODO: use shopifyModelMetadataForVersion to get version-aware identifyByRemoteKeys
      // For now this is okay since the only other models using version based remoteKeys are using the "id" graphQLFieldKeys which is protected by our id check
      const identifyByRemoteKeys = getIdentificationKeysForModel(field.model.key);
      if (identifyByRemoteKeys) {
        // don't allow fields used in identifyByRemoteKeys to be removed as these are needed to uniquely identify the record and removing them breaks the model
        if (
          (field.shopifyFieldKey && identifyByRemoteKeys.includes(field.shopifyFieldKey)) ||
          (field.shopifyGraphQLFieldKey && identifyByRemoteKeys.includes(field.shopifyGraphQLFieldKey))
        ) {
          return false;
        }
      }
      return true;
    }
  },
  supportsFieldDuplication() {
    return false;
  },
  supportsFieldTypeChanges() {
    return false;
  },
  supportsFieldRename() {
    return false;
  },
  supportsFieldFeature() {
    return false;
  },
  displaysFieldDataOffsite() {
    return true;
  },
  fieldIsWritablePublicly(field: ModelField) {
    return field.contributorConfiguration[GadgetSystemDominionId]?.isWritablePublicly ?? true;
  },
  fieldIsWritableInternally(field: ModelField) {
    return field.contributorConfiguration[GadgetSystemDominionId]?.isWritableInternally ?? true;
  },
};
